import axios, { AxiosInstance } from "axios";

const PUBLIC_API_BASE_URL = "/ofc/api/v1/tax-classifications";

export interface TaxClassification {
  id?: number;
  displayText: string;
  ledgerText: string;
  taxPercent?: number | null;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

class TaxClassificationAPI {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: PUBLIC_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  /**
   * Get all active tax classifications
   */
  async getAllActive(): Promise<TaxClassification[]> {
    try {
      const response =
        await this.axiosInstance.get<ApiResponse<TaxClassification[]>>("");
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching active tax classifications:", error);
      throw error;
    }
  }

  /**
   * Get all tax classifications (admin only)
   */
  async getAll(): Promise<TaxClassification[]> {
    try {
      const response =
        await this.axiosInstance.get<ApiResponse<TaxClassification[]>>(
          `/admin/all`,
        );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching all tax classifications:", error);
      throw error;
    }
  }

  /**
   * Get a specific tax classification by ID (admin only)
   */
  async getById(id: number): Promise<TaxClassification> {
    try {
      const response = await this.axiosInstance.get<
        ApiResponse<TaxClassification>
      >(`/admin/${id}`);
      return response.data.data!;
    } catch (error) {
      console.error(`Error fetching tax classification ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new tax classification (admin only)
   */
  async create(data: TaxClassification): Promise<TaxClassification> {
    try {
      const response = await this.axiosInstance.post<
        ApiResponse<TaxClassification>
      >(`/admin`, data);
      return response.data.data!;
    } catch (error) {
      console.error("Error creating tax classification:", error);
      throw error;
    }
  }

  /**
   * Update an existing tax classification (admin only)
   */
  async update(
    id: number,
    data: TaxClassification,
  ): Promise<TaxClassification> {
    try {
      const response = await this.axiosInstance.put<
        ApiResponse<TaxClassification>
      >(`/admin/${id}`, data);
      return response.data.data!;
    } catch (error) {
      console.error(`Error updating tax classification ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a tax classification (admin only)
   */
  async delete(id: number): Promise<void> {
    try {
      await this.axiosInstance.delete(`/admin/${id}`);
    } catch (error) {
      console.error(`Error deleting tax classification ${id}:`, error);
      throw error;
    }
  }

  /**
   * Reorder tax classifications (admin only)
   */
  async reorder(ids: number[]): Promise<void> {
    try {
      await this.axiosInstance.post(`/admin/reorder`, ids);
    } catch (error) {
      console.error("Error reordering tax classifications:", error);
      throw error;
    }
  }
}

export default new TaxClassificationAPI();
